import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../api/client.js';
import {
  browserSourceUrl,
  createWidget,
  deleteWidget,
  fetchCatalog,
  fetchWidgets,
  resetWidgetGoal,
  rotateWidgetToken,
  updateWidget,
  type WidgetCatalogEntry,
  type WidgetInstance,
} from '../api/widgets.js';
import type { PlatformName } from '../api/types.js';
import { Alert } from '../components/Alert.js';
import { PlatformIcon } from '../components/PlatformIcon.js';
import { WidgetTypeIcon } from './widgets/WidgetTypeIcon.js';
import { EmptyState } from '../components/EmptyState.js';
import { StatusPill } from '../components/StatusPill.js';
import { Tooltip } from '../components/Tooltip.js';
import { useSession } from '../auth/session-context.js';
import { WidgetEditor, type DraftConfig } from './widgets/WidgetEditor.js';
import { WidgetGallery } from './widgets/WidgetGallery.js';
import './widgets.css';

/**
 * Connaro Widgets, in the Console (ARCHITECTURE.md §103).
 *
 * ==================== WHAT THIS PAGE WILL NOT DO ====================
 * Decide anything. Which widgets exist, which platforms can drive each one,
 * whether a configuration is valid and whether the workspace's plan includes
 * the module are all answered by the server and rendered as given. There is no
 * capability table in this directory and no per-widget platform list.
 * ====================================================================
 *
 * ==================== THE TOKEN IS SHOWN ONCE ====================
 * A browser-source URL is a credential: anyone holding it can render the
 * workspace's live event feed until it is rotated. The plaintext exists in the
 * response that mints it and is never recoverable, so this page shows it once,
 * says plainly what it is, and offers rotation rather than a reveal.
 * =================================================================
 */

type Tab = 'gallery' | 'mine';

/**
 * Readable names for the stored enum.
 *
 * `DONATION_GOAL`.replace(/_/g,' ') reads as "DONATION GOAL", which is a
 * database value wearing a space. An operator reads a product.
 */
const WIDGET_LABELS: Record<string, string> = {
  ACTIVITY_FEED: 'Activity feed',
  CHAT_OVERLAY: 'Chat overlay',
  EVENT_LABELS: 'Event label',
  FOLLOWER_GOAL: 'Follower goal',
  SUBSCRIBER_GOAL: 'Subscriber goal',
  DONATION_GOAL: 'Donation goal',
  MULTI_PLATFORM_GOAL: 'Multi-platform goal',
};

/**
 * The platforms a saved widget targets.
 *
 * Read from the stored configuration rather than kept in a parallel column,
 * and defensive about its shape: a row written before a schema change is
 * exactly the case that broke a Chat Overlay on 2026-09-11, and a list page
 * must degrade to showing no marks rather than crashing the page.
 */
/**
 * The catalog entry a saved widget belongs to.
 *
 * Returns undefined rather than falling back to the first entry: editing a
 * donation goal against the activity feed's rules would offer the wrong
 * platforms and the wrong fields, which is worse than not opening the form.
 * A widget whose type is no longer in the catalog can still be deleted.
 */
function entryFor(
  catalog: readonly WidgetCatalogEntry[],
  widget: WidgetInstance,
): WidgetCatalogEntry | undefined {
  return catalog.find((item) => item.type === widget.widgetType);
}

function platformsOf(widget: WidgetInstance): PlatformName[] {
  const raw = (widget.config as { platforms?: unknown }).platforms;
  return Array.isArray(raw) ? (raw as PlatformName[]) : [];
}

export function WidgetsPage(): React.JSX.Element {
  const { activeWorkspace, can } = useSession();
  const workspaceId = activeWorkspace?.id ?? null;

  const [tab, setTab] = useState<Tab>('gallery');
  const [catalog, setCatalog] = useState<WidgetCatalogEntry[] | null>(null);
  const [widgets, setWidgets] = useState<WidgetInstance[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notEntitled, setNotEntitled] = useState(false);

  const [creating, setCreating] = useState<WidgetCatalogEntry | null>(null);
  /** The widget being edited, alongside its catalog entry for the rules. */
  const [editing, setEditing] = useState<WidgetInstance | null>(null);
  const [saving, setSaving] = useState(false);
  /** Shown once, then gone. See the note at the top of this file. */
  const [freshUrl, setFreshUrl] = useState<{ name: string; url: string } | null>(null);

  /*
   * Resolved once. A widget whose type has left the catalog cannot be edited
   * against another type's rules, so the form simply does not open for it —
   * it can still be turned off or deleted.
   */
  const editingEntry =
    editing === null || catalog === null ? undefined : entryFor(catalog, editing);

  const mayManage = can('widgets.manage');
  const mayRotate = can('widgets.tokens.rotate');

  const load = useCallback(async (): Promise<void> => {
    setError(null);

    try {
      const [loadedCatalog, loadedWidgets] = await Promise.all([fetchCatalog(), fetchWidgets()]);
      setCatalog(loadedCatalog);
      setWidgets(loadedWidgets);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'ENTITLEMENT_REQUIRED') {
        setNotEntitled(true);
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not read your widgets.');
    }
  }, []);

  useEffect(() => {
    // Nothing from the previous workspace survives the switch (§13).
    setCatalog(null);
    setWidgets(null);
    setNotEntitled(false);
    setFreshUrl(null);
    setCreating(null);
    setEditing(null);
    void load();
  }, [load, workspaceId]);

  if (notEntitled) {
    return (
      <section className="panel" aria-labelledby="widgets-heading">
        <div className="panel__header">
          <h1 className="panel__title" id="widgets-heading">
            Widgets
          </h1>
        </div>
        <div className="panel__body">
          <EmptyState title="Not included in this plan">
            <p>
              Connaro Widgets puts overlays on your stream — activity feeds, goals and chat. It is
              included from the Pro plan upwards. Nothing is lost by upgrading later.
            </p>
          </EmptyState>
        </div>
      </section>
    );
  }

  const handleCreate = async (name: string, config: DraftConfig): Promise<void> => {
    if (creating === null) return;
    setSaving(true);
    setError(null);

    try {
      const result = await createWidget({ widgetType: creating.type, name, config });
      setFreshUrl({ name: result.widget.name, url: browserSourceUrl(result.token) });
      setCreating(null);
      setTab('mine');
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Connaro could not create that widget.',
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Save an edit.
   *
   * The name and the configuration go in ONE request, because they are one
   * change from the operator's point of view — two would leave a widget
   * renamed but not reconfigured if the second failed.
   */
  const handleEdit = async (name: string, config: DraftConfig): Promise<void> => {
    if (editing === null) return;
    setSaving(true);
    setError(null);

    try {
      await updateWidget(editing.id, { name, config });
      setEditing(null);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not save that widget.');
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async (widget: WidgetInstance): Promise<void> => {
    const confirmed = window.confirm(
      `Rotating this URL stops the current Browser Source receiving data immediately. You will have to paste the new URL into OBS or Meld. Rotate “${widget.name}”?`,
    );
    if (!confirmed) return;

    try {
      const token = await rotateWidgetToken(widget.id);
      setFreshUrl({ name: widget.name, url: browserSourceUrl(token) });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not rotate that URL.');
    }
  };

  const handleDelete = async (widget: WidgetInstance): Promise<void> => {
    const confirmed = window.confirm(
      `Deleting “${widget.name}” stops its Browser Source immediately and cannot be undone. Delete it?`,
    );
    if (!confirmed) return;

    try {
      await deleteWidget(widget.id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not delete that widget.');
    }
  };

  const handleReset = async (widget: WidgetInstance): Promise<void> => {
    const confirmed = window.confirm(
      `Reset the progress on “${widget.name}” back to its starting value?`,
    );
    if (!confirmed) return;

    try {
      await resetWidgetGoal(widget.id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not reset that goal.');
    }
  };

  const handleToggle = async (widget: WidgetInstance): Promise<void> => {
    try {
      await updateWidget(widget.id, { enabled: !widget.enabled });
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Connaro could not update that widget.');
    }
  };

  return (
    <section className="panel widgets" aria-labelledby="widgets-heading">
      <div className="panel__header">
        <h1 className="panel__title" id="widgets-heading">
          Widgets
        </h1>
        <p className="panel__subtitle">Live overlays powered by your Connaro events.</p>
      </div>

      <div className="panel__body">
        {error !== null && <Alert tone="error">{error}</Alert>}

        {freshUrl !== null && (
          <Alert tone="success">
            <strong>Browser source for “{freshUrl.name}”</strong>
            <p className="widgets__url">
              <code>{freshUrl.url}</code>
            </p>
            <p>
              Add this as a <strong>Browser Source</strong> in OBS, Meld or any compatible
              software. <strong>Anyone with this URL can view this widget</strong> until you rotate
              it — and Connaro cannot show it to you again, so copy it now.
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => {
                void navigator.clipboard.writeText(freshUrl.url).catch(() => {
                  // A browser that refuses clipboard access must not swallow
                  // the URL: it is still on screen to select by hand.
                });
              }}
            >
              Copy URL
            </button>{' '}
            <button
              type="button"
              className="btn"
              onClick={() => {
                setFreshUrl(null);
              }}
            >
              Done
            </button>
          </Alert>
        )}

        <nav className="widgets__tabs" aria-label="Widget sections">
          <ul className="widgets__tablist">
            {(
              [
                ['gallery', 'Gallery'],
                ['mine', 'My widgets'],
              ] as const
            ).map(([id, label]) => (
              <li key={id}>
                <button
                  type="button"
                  className={`widgets__tab${tab === id ? ' widgets__tab--active' : ''}`}
                  aria-current={tab === id ? 'page' : undefined}
                  onClick={() => {
                    setTab(id);
                    setCreating(null);
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {catalog === null || widgets === null ? (
          <p className="field__hint">Loading…</p>
        ) : editingEntry !== undefined && editing !== null ? (
          <WidgetEditor
            entry={editingEntry}
            existing={editing}
            saving={saving}
            onCancel={() => {
              setEditing(null);
            }}
            onSave={(name, config) => {
              void handleEdit(name, config);
            }}
          />
        ) : creating !== null ? (
          <WidgetEditor
            entry={creating}
            saving={saving}
            onCancel={() => {
              setCreating(null);
            }}
            onSave={(name, config) => {
              void handleCreate(name, config);
            }}
          />
        ) : tab === 'gallery' ? (
          <WidgetGallery
            catalog={catalog}
            mayManage={mayManage}
            onCreate={(entry) => {
              setCreating(entry);
            }}
          />
        ) : widgets.length === 0 ? (
          <EmptyState title="You haven’t created any widgets yet">
            Choose one from the gallery to add to your stream.
          </EmptyState>
        ) : (
          <ul className="widgets__list">
            {widgets.map((widget) => (
              <li key={widget.id} className="widgets__row">
                <span className="widgets__row-mark" aria-hidden="true">
                  <WidgetTypeIcon type={widget.widgetType} size={20} />
                </span>

                <div className="widgets__row-main">
                  <span className="widgets__row-name">{widget.name}</span>
                  <span className="widgets__row-meta">
                    <span className="widgets__row-type">
                      {WIDGET_LABELS[widget.widgetType] ?? widget.widgetType.replace(/_/gu, ' ')}
                    </span>
                    {platformsOf(widget).map((platform) => (
                      <PlatformIcon
                        key={platform}
                        platform={platform}
                        displayName={platform}
                        size={16}
                        label={platform}
                      />
                    ))}
                  </span>
                </div>

                <StatusPill
                  tone={widget.enabled ? 'ok' : 'idle'}
                  label={widget.enabled ? 'On' : 'Off'}
                />

                <div className="widgets__row-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!mayManage}
                    onClick={() => {
                      setEditing(widget);
                    }}
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    className="btn"
                    disabled={!mayManage}
                    onClick={() => {
                      void handleToggle(widget);
                    }}
                  >
                    {widget.enabled ? 'Turn off' : 'Turn on'}
                  </button>

                  {widget.widgetType.endsWith('GOAL') && (
                    <button
                      type="button"
                      className="btn"
                      disabled={!mayManage}
                      onClick={() => {
                        void handleReset(widget);
                      }}
                    >
                      Reset progress
                    </button>
                  )}

                  <Tooltip tip="Issues a new URL and stops the old one immediately. You will need to update OBS or Meld.">
                    <button
                      type="button"
                      className="btn"
                      disabled={!mayRotate}
                      onClick={() => {
                        void handleRotate(widget);
                      }}
                    >
                      Rotate URL
                    </button>
                  </Tooltip>

                  <button
                    type="button"
                    className="btn btn--danger"
                    disabled={!mayManage}
                    onClick={() => {
                      void handleDelete(widget);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
